import { resolveOpenPencilApiBase } from "./openpencil-api-base";
import { getStudioAccessToken } from "@/services/auth-provider";
import {
  classifyOpenPencilImportFailure,
  type OpenPencilImportFailureReason,
} from "./openpencil-import-failure";
import {
  buildBlockedOpenPencilFigmaSourceBundle,
  normalizeOpenPencilFigmaSourceBundle,
  OpenPencilBoundedVisualJudgementSchema,
  OpenPencilFigEditPatchSchema,
  OpenPencilFigRevisionEvidenceSchema,
  OpenPencilRuntimeProofResultSchema,
  type OpenPencilBoundedVisualJudgement,
  type OpenPencilFigEditPatch,
  type OpenPencilFigRevisionEvidence,
  type OpenPencilRuntimeProofResult,
  type OpenPencilFigmaSourceBundle,
  type OpenPencilSurfacePreviewArtifact,
} from "./openpencil-figma-source";

const POLL_INTERVAL_MS = 1_000;
const IMPORT_TIMEOUT_MS = 90_000;
const VISUAL_JUDGEMENT_TIMEOUT_MS = 180_000;
const SOURCE_REVISION_TIMEOUT_MS = 240_000;

export interface ImportOpenPencilFigmaFileInput {
  file: File;
  sourceName: string;
  workspaceId?: string;
  signal?: AbortSignal;
  onStatus?: (status: string) => void;
  /**
   * Typed failure channel (UX audit, 2026-07-27). `importOpenPencilFigmaFile`
   * still resolves with a blocked bundle so every existing caller is
   * unchanged; this callback additionally hands the caller the CLOSED reason
   * it must render, so no surface has to re-derive one from a raw body.
   */
  onFailure?: (failure: OpenPencilImportFailure) => void;
}

export interface OpenPencilImportFailure {
  reason: OpenPencilImportFailureReason;
  /** Retained transport detail — evidence/logging only, never creator copy. */
  detail: string;
}

/**
 * Transport error carrying the signal the typed classifier needs. The message
 * is byte-identical to what the client threw before, so the blocked bundle's
 * retained `reason` evidence is unchanged.
 */
class OpenPencilImportTransportError extends Error {
  readonly status: number | null;
  readonly code: string | null;

  constructor(message: string, status: number | null, code: string | null) {
    super(message);
    this.name = "OpenPencilImportTransportError";
    this.status = status;
    this.code = code;
  }
}

/** Pull the typed code out of a Pages-Function/Worker error body. */
function transportCode(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const body = payload as { code?: unknown; error?: unknown };
  if (typeof body.code === "string" && body.code.trim()) return body.code.trim();
  if (typeof body.error === "string" && body.error.trim())
    return body.error.trim();
  return null;
}

function importTransportError(
  payload: unknown,
  status: number,
  fallback: string,
  /**
   * Code to attribute when the body carries none of its own — i.e. the
   * failure came from the transport rather than from the import service.
   */
  fallbackCode: string | null = null,
): OpenPencilImportTransportError {
  const body = (payload ?? {}) as { error?: unknown };
  const message =
    typeof body.error === "string" && body.error.trim()
      ? body.error
      : fallback;
  return new OpenPencilImportTransportError(
    message,
    status,
    transportCode(payload) ?? fallbackCode,
  );
}

interface ImportJobResponse {
  job?: {
    jobId?: string;
    status?: string;
    error?: string;
  };
  upload?: {
    method?: string;
    url?: string;
  };
  error?: string;
}

interface VisualJudgementJobResponse {
  job?: {
    judgementJobId?: string;
    status?: string;
    error?: string;
  };
  result?: unknown;
  error?: string;
}

interface RuntimeProofJobResponse {
  job?: {
    proofJobId?: string;
    status?: string;
    error?: string;
  };
  result?: unknown;
  error?: string;
}

interface SourceRevisionJobResponse {
  job?: {
    revisionJobId?: string;
    status?: string;
    resultImportJobId?: string;
    evidence?: unknown;
    error?: string;
  };
  error?: string;
}

export interface OpenPencilFigRevisionResult {
  bundle: OpenPencilFigmaSourceBundle;
  evidence: OpenPencilFigRevisionEvidence;
}

function apiBaseUrl(): string | null {
  return resolveOpenPencilApiBase(
    (import.meta.env as Record<string, string | undefined>)
      .VITE_OPENPENCIL_IMPORT_API_URL,
    typeof window === "undefined" ? "" : window.location.origin,
  );
}

/**
 * Whether a .fig import attempted here can reach ANY importer.
 *
 * `importOpenPencilFigmaFile` has exactly two legs: the production service
 * (needs `VITE_OPENPENCIL_IMPORT_API_URL` — the appliance build sets it empty
 * on purpose, so a closed network reports the feature unavailable instead of
 * dialling out) and the dev-only local adapter (exists only under the Vite dev
 * server). When neither leg exists, every import is a guaranteed dead-end —
 * so a surface that ADVERTISES Figma intake should ask this first, rather
 * than let the reader pick a file and watch it fail.
 */
export function isOpenPencilFigmaImportAvailable(): boolean {
  return apiBaseUrl() !== null || Boolean(import.meta.env.DEV);
}

function exactRevisionRecord(
  actual: Record<string, string | number | boolean | null>,
  expected: Record<string, string | number | boolean | null>,
): boolean {
  const actualKeys = Object.keys(actual).sort();
  const expectedKeys = Object.keys(expected).sort();
  return (
    actualKeys.length === expectedKeys.length &&
    actualKeys.every(
      (key, index) =>
        key === expectedKeys[index] && Object.is(actual[key], expected[key]),
    )
  );
}

function normalizeOpaqueHex(value: string): string {
  const normalized = value.toUpperCase();
  return normalized.length === 9 && normalized.endsWith("FF")
    ? normalized.slice(0, 7)
    : normalized;
}

async function revisionAfterMatches(
  operation: OpenPencilFigEditPatch["operations"][number],
  appliedAfter: Record<string, string | number | boolean | null>,
  baseBundle: OpenPencilFigmaSourceBundle,
): Promise<boolean> {
  switch (operation.operation) {
    case "rename-node":
      return exactRevisionRecord(appliedAfter, { name: operation.name });
    case "set-text":
      return exactRevisionRecord(appliedAfter, {
        length: operation.text.length,
        sha256: await textSha256(operation.text),
      });
    case "set-visible":
      return exactRevisionRecord(appliedAfter, { visible: operation.visible });
    case "set-opacity":
      return exactRevisionRecord(appliedAfter, { opacity: operation.opacity });
    case "set-solid-fill":
      return exactRevisionRecord(appliedAfter, {
        fillIndex: operation.fillIndex,
        ...(operation.color !== undefined
          ? { color: normalizeOpaqueHex(operation.color) }
          : {}),
        ...(operation.fillOpacity !== undefined
          ? { fillOpacity: operation.fillOpacity }
          : {}),
      });
    case "set-geometry":
      return exactRevisionRecord(appliedAfter, {
        ...(operation.x !== undefined ? { x: operation.x } : {}),
        ...(operation.y !== undefined ? { y: operation.y } : {}),
        ...(operation.width !== undefined ? { width: operation.width } : {}),
        ...(operation.height !== undefined ? { height: operation.height } : {}),
        ...(operation.rotation !== undefined
          ? { rotation: operation.rotation }
          : {}),
      });
    case "set-variable-mode-value":
      if (typeof operation.value === "object") {
        const requestedAliasId = operation.value.aliasId;
        const requestedAlias =
          baseBundle.readback?.variables.samples.filter(
            (variable) => variable.id === requestedAliasId,
          ) ?? [];
        if (requestedAlias.length !== 1) {
          throw new Error(
            `Requested variable alias target ${requestedAliasId} is not uniquely present in the base source readback.`,
          );
        }
        // Variable IDs can be remapped by .fig export, so bind the requested
        // alias ID to the base readback's stable semantic identity and require
        // the exported evidence to name that exact variable and collection.
        const aliasTarget = requestedAlias[0];
        return (
          appliedAfter.valueKind === "alias" &&
          appliedAfter.aliasVariableName === aliasTarget.name &&
          appliedAfter.aliasCollectionName === aliasTarget.collectionName &&
          (aliasTarget.sourceKey
            ? appliedAfter.aliasVariableKey === aliasTarget.sourceKey
            : appliedAfter.aliasVariableKey === undefined)
        );
      }
      return exactRevisionRecord(appliedAfter, {
        valueKind: "literal",
        valueType: operation.expectedVariableType,
        value:
          operation.expectedVariableType === "COLOR"
            ? normalizeOpaqueHex(String(operation.value))
            : operation.value,
      });
  }
}

async function validateRevisionOperations(
  baseBundle: OpenPencilFigmaSourceBundle,
  patch: OpenPencilFigEditPatch,
  evidence: OpenPencilFigRevisionEvidence,
): Promise<void> {
  if (evidence.operations.length !== patch.operations.length) {
    throw new Error(
      "Revised source operation evidence count does not match the requested patch.",
    );
  }
  for (let index = 0; index < patch.operations.length; index += 1) {
    const requested = patch.operations[index];
    const applied = evidence.operations[index];
    const identityMatches =
      requested.operation === "set-variable-mode-value"
        ? applied.operation === "set-variable-mode-value" &&
          applied.variableId === requested.variableId &&
          applied.collectionId === requested.collectionId &&
          applied.modeId === requested.modeId &&
          applied.variableType === requested.expectedVariableType
        : applied.operation !== "set-variable-mode-value" &&
          (requested.sourceNodeId
            ? applied.sourceNodeId === requested.sourceNodeId
            : applied.nodeId === requested.nodeId) &&
          applied.nodeType === requested.expectedNodeType;
    if (
      applied.index !== index ||
      !identityMatches ||
      applied.operation !== requested.operation
    ) {
      throw new Error(
        `Revised source operation evidence does not match requested patch at index ${index}.`,
      );
    }
    if (!(await revisionAfterMatches(requested, applied.after, baseBundle))) {
      throw new Error(
        `Revised source operation result does not match requested after value at index ${index}.`,
      );
    }
  }
}

async function validateRevisionResult(
  baseBundle: OpenPencilFigmaSourceBundle,
  patchValue: OpenPencilFigEditPatch,
  bundleValue: unknown,
  evidenceValue: unknown,
  signal?: AbortSignal,
): Promise<OpenPencilFigRevisionResult> {
  const patch = OpenPencilFigEditPatchSchema.parse(patchValue);
  const evidence = OpenPencilFigRevisionEvidenceSchema.parse(evidenceValue);
  const bundle = normalizeOpenPencilFigmaSourceBundle(bundleValue);
  if (
    patch.baseSourceId !== baseBundle.sourceId ||
    patch.baseSha256 !== baseBundle.fileSha256 ||
    evidence.patchId !== patch.patchId ||
    evidence.baseSourceId !== baseBundle.sourceId ||
    evidence.baseSha256 !== baseBundle.fileSha256 ||
    evidence.outputSha256 !== bundle.fileSha256 ||
    evidence.outputFileName !== bundle.fileName ||
    bundle.status !== "ready" ||
    bundle.sourceId === baseBundle.sourceId ||
    bundle.fileSha256 === baseBundle.fileSha256 ||
    !bundle.sourceArtifactRef ||
    bundle.sourceArtifactRef === baseBundle.sourceArtifactRef ||
    bundle.readback?.sourceArtifactRef !== bundle.sourceArtifactRef ||
    !evidence.structuralIntegrityPreserved
  ) {
    throw new Error("Revised source evidence does not match its authority.");
  }
  const lineage = bundle.revisionLineage;
  if (
    !lineage ||
    lineage.parentSourceId !== baseBundle.sourceId ||
    lineage.parentSha256 !== baseBundle.fileSha256 ||
    lineage.patchId !== patch.patchId ||
    !lineage.evidenceRef ||
    lineage.evidenceRef === baseBundle.revisionLineage?.evidenceRef ||
    evidence.outputSourceId !== bundle.sourceId ||
    evidence.outputArtifactRef !== bundle.sourceArtifactRef ||
    evidence.evidenceRef !== lineage.evidenceRef ||
    lineage.sourceRevision !==
      (baseBundle.revisionLineage?.sourceRevision ?? 0) + 1
  ) {
    throw new Error("Revised source lineage is missing or inconsistent.");
  }
  await validateRevisionOperations(baseBundle, patch, evidence);
  const retainedOutput = await loadOpenPencilSourceFile(bundle, signal);
  if (retainedOutput.size !== evidence.outputSizeBytes) {
    throw new Error(
      "Revised source byte count does not match retained output evidence.",
    );
  }
  return { bundle, evidence };
}

async function blobSha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await blob.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function textSha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function loadOpenPencilSourceFile(
  bundle: OpenPencilFigmaSourceBundle,
  signal?: AbortSignal,
): Promise<Blob> {
  if (!bundle.fileSha256) {
    throw new Error("Source export requires the retained import checksum.");
  }
  let response: Response;
  if (bundle.sourceArtifactRef?.startsWith("local-artifact://openpencil/")) {
    response = await fetch(
      `/api/openpencil/figma/artifacts/${encodeURIComponent(bundle.sourceId)}/source`,
      { signal },
    );
  } else {
    const base = apiBaseUrl();
    const accessToken = await getStudioAccessToken();
    if (!base || !accessToken) {
      throw new Error("Authenticated source export is not configured.");
    }
    response = await fetch(
      `${base}/v1/imports/${encodeURIComponent(bundle.sourceId)}/source`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal,
      },
    );
  }
  if (!response.ok) {
    throw new Error(`Source artifact returned ${response.status}.`);
  }
  const blob = await response.blob();
  const actual = await blobSha256(blob);
  if (actual !== bundle.fileSha256) {
    throw new Error("Exported source checksum does not match the import.");
  }
  return blob;
}

export async function downloadOpenPencilSourceFile(
  bundle: OpenPencilFigmaSourceBundle,
): Promise<{ fileName: string; sizeBytes: number; sha256: string }> {
  const blob = await loadOpenPencilSourceFile(bundle);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = bundle.fileName;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
  return {
    fileName: bundle.fileName,
    sizeBytes: blob.size,
    sha256: bundle.fileSha256!,
  };
}

export async function loadOpenPencilSurfacePreview(
  bundle: OpenPencilFigmaSourceBundle,
  preview: OpenPencilSurfacePreviewArtifact,
  signal?: AbortSignal,
): Promise<Blob> {
  let response: Response;
  if (preview.artifactRef.startsWith("local-artifact://openpencil/")) {
    response = await fetch(
      `/api/openpencil/figma/artifacts/${encodeURIComponent(bundle.sourceId)}/previews/${encodeURIComponent(preview.fileName)}`,
      { signal },
    );
  } else {
    const base = apiBaseUrl();
    const accessToken = await getStudioAccessToken();
    if (!base || !accessToken) {
      throw new Error("Authenticated preview access is not configured.");
    }
    response = await fetch(
      `${base}/v1/imports/${encodeURIComponent(bundle.sourceId)}/previews/${encodeURIComponent(preview.fileName)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal,
      },
    );
  }
  if (!response.ok) {
    throw new Error(`Preview artifact returned ${response.status}.`);
  }
  const blob = await response.blob();
  if (blob.type && blob.type !== "image/png") {
    throw new Error(`Unexpected preview content type: ${blob.type}`);
  }
  return blob;
}

export async function requestOpenPencilVisualJudgement(input: {
  bundle: OpenPencilFigmaSourceBundle;
  surfaceIds: string[];
  signal?: AbortSignal;
  onStatus?: (status: string) => void;
}): Promise<OpenPencilBoundedVisualJudgement> {
  const base = apiBaseUrl();
  const accessToken = await getStudioAccessToken();
  if (!base || !accessToken) {
    throw new Error(
      "Production OpenPencil import and Gemma visual judgement are not configured.",
    );
  }
  const surfaceIds = [...new Set(input.surfaceIds)];
  if (surfaceIds.length === 0) {
    throw new Error(
      "Select at least one source screen before visual judgement.",
    );
  }
  const timeout = new AbortController();
  const timeoutId = window.setTimeout(
    () =>
      timeout.abort(
        new DOMException("Visual judgement timed out", "TimeoutError"),
      ),
    VISUAL_JUDGEMENT_TIMEOUT_MS,
  );
  const signal = input.signal
    ? AbortSignal.any([input.signal, timeout.signal])
    : timeout.signal;
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
  try {
    input.onStatus?.("Queueing bounded visual review");
    const created = await fetch(
      `${base}/v1/imports/${encodeURIComponent(input.bundle.sourceId)}/visual-judgements`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ surfaceIds }),
        signal,
      },
    );
    const createdPayload = (await created
      .json()
      .catch(() => ({}))) as VisualJudgementJobResponse;
    const judgementJobId = createdPayload.job?.judgementJobId;
    if (!created.ok || !judgementJobId) {
      throw new Error(
        createdPayload.error ||
          `Visual judgement API returned ${created.status}.`,
      );
    }
    while (!signal.aborted) {
      input.onStatus?.("Reviewing source previews with Gemma");
      const response = await fetch(
        `${base}/v1/imports/${encodeURIComponent(input.bundle.sourceId)}/visual-judgements/${encodeURIComponent(judgementJobId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as VisualJudgementJobResponse;
      if (!response.ok) {
        throw new Error(
          payload.error ||
            `Visual judgement status returned ${response.status}.`,
        );
      }
      if (payload.job?.status === "ready") {
        return OpenPencilBoundedVisualJudgementSchema.parse(payload.result);
      }
      if (
        payload.job?.status === "blocked" ||
        payload.job?.status === "cancelled"
      ) {
        throw new Error(
          payload.job.error || `Visual judgement is ${payload.job.status}.`,
        );
      }
      await delay(POLL_INTERVAL_MS, signal);
    }
    throw signal.reason ?? new Error("Visual judgement was aborted.");
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function requestOpenPencilRuntimeProof(input: {
  bundle: OpenPencilFigmaSourceBundle;
  manifestId: string;
  previewUrl: string;
  applicationArtifactRef: string;
  signal?: AbortSignal;
  onStatus?: (status: string) => void;
}): Promise<OpenPencilRuntimeProofResult> {
  const base = apiBaseUrl();
  const accessToken = await getStudioAccessToken();
  if (!base || !accessToken) {
    throw new Error("Production runtime proof service is not configured.");
  }
  const timeout = new AbortController();
  const timeoutId = window.setTimeout(
    () =>
      timeout.abort(
        new DOMException("Runtime proof timed out", "TimeoutError"),
      ),
    VISUAL_JUDGEMENT_TIMEOUT_MS,
  );
  const signal = input.signal
    ? AbortSignal.any([input.signal, timeout.signal])
    : timeout.signal;
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
  try {
    input.onStatus?.("Queueing headless runtime proof");
    const created = await fetch(
      `${base}/v1/imports/${encodeURIComponent(input.bundle.sourceId)}/runtime-proofs`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          manifestId: input.manifestId,
          previewUrl: input.previewUrl,
          applicationArtifactRef: input.applicationArtifactRef,
        }),
        signal,
      },
    );
    const createdPayload = (await created
      .json()
      .catch(() => ({}))) as RuntimeProofJobResponse;
    const proofJobId = createdPayload.job?.proofJobId;
    if (!created.ok || !proofJobId) {
      throw new Error(
        createdPayload.error || `Runtime proof API returned ${created.status}.`,
      );
    }
    while (!signal.aborted) {
      input.onStatus?.("Running desktop and mobile proof");
      const response = await fetch(
        `${base}/v1/imports/${encodeURIComponent(input.bundle.sourceId)}/runtime-proofs/${encodeURIComponent(proofJobId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RuntimeProofJobResponse;
      if (!response.ok) {
        throw new Error(
          payload.error || `Runtime proof status returned ${response.status}.`,
        );
      }
      if (payload.job?.status === "ready") {
        return OpenPencilRuntimeProofResultSchema.parse(payload.result);
      }
      if (
        payload.job?.status === "blocked" ||
        payload.job?.status === "cancelled"
      ) {
        throw new Error(
          payload.job.error || `Runtime proof is ${payload.job.status}.`,
        );
      }
      await delay(POLL_INTERVAL_MS, signal);
    }
    throw signal.reason ?? new Error("Runtime proof was aborted.");
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function requestOpenPencilFigRevision(input: {
  bundle: OpenPencilFigmaSourceBundle;
  patch: OpenPencilFigEditPatch;
  signal?: AbortSignal;
  onStatus?: (status: string) => void;
}): Promise<OpenPencilFigRevisionResult> {
  const patch = OpenPencilFigEditPatchSchema.parse(input.patch);
  if (
    !input.bundle.fileSha256 ||
    patch.baseSourceId !== input.bundle.sourceId ||
    patch.baseSha256 !== input.bundle.fileSha256
  ) {
    throw new Error("Figma edit patch does not match the retained source.");
  }
  if (!input.bundle.fileName.toLowerCase().endsWith(".fig")) {
    throw new Error("Only retained .fig sources can be revised.");
  }

  input.onStatus?.("Applying bounded source edit");
  if (input.bundle.sourceArtifactRef?.startsWith("local-artifact://")) {
    const response = await fetch(
      `/api/openpencil/figma/artifacts/${encodeURIComponent(input.bundle.sourceId)}/revisions`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
        signal: input.signal,
      },
    );
    const payload = (await response.json().catch(() => ({}))) as {
      bundle?: unknown;
      evidence?: unknown;
      error?: string;
    };
    if (!response.ok) {
      throw new Error(
        payload.error || `Local source revision returned ${response.status}.`,
      );
    }
    input.onStatus?.("Verifying the revised design");
    return validateRevisionResult(
      input.bundle,
      patch,
      payload.bundle,
      payload.evidence,
      input.signal,
    );
  }

  const base = apiBaseUrl();
  const accessToken = await getStudioAccessToken();
  if (!base || !accessToken) {
    throw new Error("Production source revision service is not configured.");
  }
  const timeout = new AbortController();
  const timeoutId = window.setTimeout(
    () =>
      timeout.abort(
        new DOMException("Source revision timed out", "TimeoutError"),
      ),
    SOURCE_REVISION_TIMEOUT_MS,
  );
  const signal = input.signal
    ? AbortSignal.any([input.signal, timeout.signal])
    : timeout.signal;
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
  try {
    const created = await fetch(
      `${base}/v1/imports/${encodeURIComponent(input.bundle.sourceId)}/revisions`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(patch),
        signal,
      },
    );
    const createdPayload = (await created
      .json()
      .catch(() => ({}))) as SourceRevisionJobResponse;
    const revisionJobId = createdPayload.job?.revisionJobId;
    if (!created.ok || !revisionJobId) {
      throw new Error(
        createdPayload.error ||
          `Source revision API returned ${created.status}.`,
      );
    }

    let resultImportJobId = "";
    let evidenceValue: unknown;
    while (!signal.aborted) {
      input.onStatus?.("Exporting a locked Figma revision");
      const response = await fetch(
        `${base}/v1/imports/${encodeURIComponent(input.bundle.sourceId)}/revisions/${encodeURIComponent(revisionJobId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as SourceRevisionJobResponse;
      if (!response.ok) {
        throw new Error(
          payload.error ||
            `Source revision status returned ${response.status}.`,
        );
      }
      if (payload.job?.status === "ready") {
        resultImportJobId = payload.job.resultImportJobId ?? "";
        evidenceValue = payload.job.evidence;
        if (!resultImportJobId || !evidenceValue) {
          throw new Error("Source revision completed without output evidence.");
        }
        break;
      }
      if (
        payload.job?.status === "blocked" ||
        payload.job?.status === "cancelled"
      ) {
        throw new Error(
          payload.job.error || `Source revision is ${payload.job.status}.`,
        );
      }
      await delay(POLL_INTERVAL_MS, signal);
    }
    if (!resultImportJobId) {
      throw signal.reason ?? new Error("Source revision was aborted.");
    }

    while (!signal.aborted) {
      input.onStatus?.("Reading the revised design");
      const response = await fetch(
        `${base}/v1/imports/${encodeURIComponent(resultImportJobId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal },
      );
      const payload = await responsePayload(response);
      if (!response.ok) {
        throw new Error(
          payload.error || `Revised import status returned ${response.status}.`,
        );
      }
      const status = payload.job?.status;
      if (status === "ready" || status === "blocked") {
        const bundleResponse = await fetch(
          `${base}/v1/imports/${encodeURIComponent(resultImportJobId)}/bundle`,
          { headers: { Authorization: `Bearer ${accessToken}` }, signal },
        );
        const bundleValue = await bundleResponse.json().catch(() => null);
        if (!bundleResponse.ok) {
          throw new Error(
            `Revised source bundle returned ${bundleResponse.status}.`,
          );
        }
        return validateRevisionResult(
          input.bundle,
          patch,
          bundleValue,
          evidenceValue,
          signal,
        );
      }
      if (status === "cancelled") {
        throw new Error("Revised source import was cancelled.");
      }
      await delay(POLL_INTERVAL_MS, signal);
    }
    throw signal.reason ?? new Error("Source revision was aborted.");
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Resolve the upload target the service NAMED against this build's API base.
 *
 * `POST /v1/imports` answers with a ROOT-ABSOLUTE path
 * (`/v1/imports/<jobId>/source`). `new URL(path, base)` discards the base's
 * PATH for such a value, so on the appliance — where the base is
 * `<origin>/api/openpencil/figma` — the bytes were PUT to
 * `<origin>/v1/imports/<jobId>/source`. That is not the import service: it
 * answers 404, which then classified as `source-not-found` and told the
 * creator their import no longer existed, for a job the service had just
 * created. Every OTHER endpoint in this client interpolates onto `base` and
 * was therefore unaffected — this was the only leg that went through
 * `new URL`. Stripping the leading slash is what joins onto the base PATH
 * instead. An absolute `value` (a presigned target) needs no special case —
 * `new URL` returns one unchanged — so it must NOT be concatenated onto the
 * base either.
 */
function absoluteApiUrl(base: string, value: string): string {
  return new URL(value.replace(/^\/+/, ""), `${base}/`).toString();
}

function delay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

async function responsePayload(response: Response): Promise<ImportJobResponse> {
  return (await response.json().catch(() => ({}))) as ImportJobResponse;
}

async function importFromProductionService(
  base: string,
  input: ImportOpenPencilFigmaFileInput,
): Promise<OpenPencilFigmaSourceBundle> {
  const accessToken = await getStudioAccessToken();
  if (!accessToken) {
    throw new OpenPencilImportTransportError(
      "Sign in before importing a production .fig source.",
      401,
      null,
    );
  }

  const timeout = new AbortController();
  const timeoutId = window.setTimeout(
    () => timeout.abort(new DOMException("Import timed out", "TimeoutError")),
    IMPORT_TIMEOUT_MS,
  );
  const combinedSignal = input.signal
    ? AbortSignal.any([input.signal, timeout.signal])
    : timeout.signal;
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    input.onStatus?.("Hashing source file");
    const fileSha256 = await sha256(input.file);
    input.onStatus?.("Creating secure import job");
    const created = await fetch(`${base}/v1/imports`, {
      method: "POST",
      headers,
      signal: combinedSignal,
      body: JSON.stringify({
        workspaceId: input.workspaceId || "workspace_local",
        sourceName: input.sourceName,
        fileName: input.file.name,
        sizeBytes: input.file.size,
        sha256: fileSha256,
      }),
    });
    const createdPayload = await responsePayload(created);
    const jobId = createdPayload.job?.jobId;
    const uploadUrl = createdPayload.upload?.url;
    if (!created.ok || !jobId || !uploadUrl) {
      throw importTransportError(
        createdPayload,
        created.status,
        `Import job API returned ${created.status}.`,
      );
    }

    input.onStatus?.("Uploading source file");
    const uploaded = await fetch(absoluteApiUrl(base, uploadUrl), {
      method: createdPayload.upload?.method || "PUT",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: input.file,
      signal: combinedSignal,
    });
    const uploadedPayload = await responsePayload(uploaded);
    if (!uploaded.ok) {
      throw importTransportError(
        uploadedPayload,
        uploaded.status,
        `Source upload returned ${uploaded.status}.`,
        // A bodiless 404 on THIS leg cannot mean "the source is gone": the
        // job was created moments ago and the service itself named this URL.
        // It means the bytes never reached the importer. When the service
        // does answer with its own code or prose, that still wins.
        uploaded.status === 404 ? "OPENPENCIL_SOURCE_UPLOAD_FAILED" : null,
      );
    }

    input.onStatus?.("Reading the design");
    while (!combinedSignal.aborted) {
      const statusResponse = await fetch(`${base}/v1/imports/${jobId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: combinedSignal,
      });
      const statusPayload = await responsePayload(statusResponse);
      if (!statusResponse.ok) {
        throw importTransportError(
          statusPayload,
          statusResponse.status,
          `Import status API returned ${statusResponse.status}.`,
        );
      }
      const status = statusPayload.job?.status;
      input.onStatus?.(
        status === "processing" ? "Analyzing Figma source" : "Import queued",
      );
      if (status === "ready" || status === "blocked") {
        const bundleResponse = await fetch(
          `${base}/v1/imports/${jobId}/bundle`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: combinedSignal,
          },
        );
        const bundlePayload = (await bundleResponse
          .json()
          .catch(() => null)) as unknown;
        if (!bundleResponse.ok) {
          throw importTransportError(
            bundlePayload,
            bundleResponse.status,
            `Import bundle API returned ${bundleResponse.status}.`,
          );
        }
        return normalizeOpenPencilFigmaSourceBundle(bundlePayload);
      }
      if (status === "cancelled") throw new Error("Import was cancelled.");
      await delay(POLL_INTERVAL_MS, combinedSignal);
    }
    throw combinedSignal.reason ?? new Error("Import was aborted.");
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function importFromLocalAdapter(
  input: ImportOpenPencilFigmaFileInput,
): Promise<OpenPencilFigmaSourceBundle> {
  const formData = new FormData();
  formData.set("sourceName", input.sourceName);
  formData.set("file", input.file);
  const response = await fetch("/api/openpencil/figma/import", {
    method: "POST",
    body: formData,
    signal: input.signal,
  });
  const payload = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    throw importTransportError(
      payload,
      response.status,
      `OpenPencil import API returned ${response.status}.`,
    );
  }
  return normalizeOpenPencilFigmaSourceBundle(payload);
}

export async function importOpenPencilFigmaFile(
  input: ImportOpenPencilFigmaFileInput,
): Promise<OpenPencilFigmaSourceBundle> {
  try {
    const productionBase = apiBaseUrl();
    return productionBase
      ? await importFromProductionService(productionBase, input)
      : await importFromLocalAdapter(input);
  } catch (error) {
    const detail =
      error instanceof Error
        ? error.message
        : "OpenPencil import API is not available.";
    // Single classification point: every failing leg above throws a transport
    // error carrying the status + typed code, so the reason handed to the UI
    // is derived from the contract, never re-sniffed from prose downstream.
    input.onFailure?.({
      reason: classifyOpenPencilImportFailure({
        message: detail,
        ...(error instanceof OpenPencilImportTransportError
          ? { status: error.status, code: error.code }
          : {}),
      }),
      detail,
    });
    return buildBlockedOpenPencilFigmaSourceBundle({
      sourceName: input.sourceName,
      fileName: input.file.name,
      reason: detail,
    });
  }
}
